const BUNDLE_WIDGET_SELECTOR = 'lb-upsell-widget, .lb-widget-bl';
const VARIANT_UPDATE_EVENT = 'variant:update';

class LuminomoSelleasyBundle extends HTMLElement {
  #observer;
  #retryTimer;
  #retryCount = 0;
  #widget;
  #activeOffer;
  #currentProduct;
  #selectedVariants = new Map();
  #currentVariantId;
  #isAdding = false;

  connectedCallback() {
    this.#currentVariantId = this.dataset.currentVariantId || '';
    this.#currentProduct = this.#readCurrentProduct();
    this.#observer = new MutationObserver(() => this.#connectToWidget());
    this.#observer.observe(document.body, { childList: true, subtree: true });

    window.addEventListener('lbWidgetRendered', this.#handleSelleasyRender);
    document.addEventListener(VARIANT_UPDATE_EVENT, this.#handleVariantUpdate);
    this.#connectToWidget();
    this.#startRetry();
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    if (this.#retryTimer) window.clearInterval(this.#retryTimer);
    window.removeEventListener('lbWidgetRendered', this.#handleSelleasyRender);
    document.removeEventListener(VARIANT_UPDATE_EVENT, this.#handleVariantUpdate);
  }

  #startRetry() {
    if (this.#retryTimer) return;

    this.#retryTimer = window.setInterval(() => {
      this.#retryCount += 1;
      this.#connectToWidget();

      if (!this.hidden || this.#retryCount >= 80) {
        window.clearInterval(this.#retryTimer);
        this.#retryTimer = null;
      }
    }, 250);
  }

  #handleSelleasyRender = () => {
    this.#connectToWidget();
  };

  #handleVariantUpdate = (event) => {
    const productId = event.detail?.data?.productId || event.detail?.data?.newProduct?.id;
    if (productId?.toString() !== this.dataset.productId?.toString()) return;

    this.#currentVariantId = event.detail?.resource?.id?.toString() || '';
    this.#selectedVariants.set(this.dataset.productId?.toString(), this.#currentVariantId);
    this.#render();
  };

  #connectToWidget() {
    const widgets = Array.from(document.querySelectorAll(BUNDLE_WIDGET_SELECTOR));
    const matches = widgets
      .map((candidate) => ({ widget: candidate, offer: this.#activeOfferForWidget(candidate) }))
      .filter((match) => match.offer);
    const match = matches[0];

    if (!match) {
      this.#debug('No active Selleasy bundle for current trigger product.', { widgets });
      return;
    }
    if (
      this.#widget === match.widget &&
      this.#offerKey(this.#activeOffer?.payload) === this.#offerKey(match.offer.payload) &&
      !this.hidden
    ) {
      return;
    }

    this.#widget = match.widget;
    this.#activeOffer = match.offer;
    this.#debug('Connected to active Selleasy bundle.', { widget: match.widget, offer: match.offer });
    this.#primeSelectedVariants();
    this.#render();
  }

  #activeOfferForWidget(widget) {
    const widgetPayload = this.#widgetPayload(widget);
    if (!widgetPayload) return null;

    const widgetTriggerIds = this.#triggerProductIds(widget, widgetPayload);
    const widgetMatchesCurrentTrigger = this.#idsIncludeCurrentProduct(widgetTriggerIds);
    const offers = this.#offerCandidates(widgetPayload);

    for (const offerPayload of offers) {
      const offerTriggerIds = this.#triggerProductIds(widget, offerPayload);
      const offerMatchesCurrentTrigger =
        this.#idsIncludeCurrentProduct(offerTriggerIds) ||
        (widgetMatchesCurrentTrigger && offerTriggerIds.length === 0);

      if (!offerMatchesCurrentTrigger) continue;

      const products = this.#productsForOffer(offerPayload);
      if (!products.length) continue;

      return {
        payload: this.#mergeInheritedOfferPayload(widgetPayload, offerPayload),
        products: this.#withCurrentProduct(products),
        triggerProductIds: offerTriggerIds.length ? offerTriggerIds : widgetTriggerIds,
      };
    }

    if (!widgetMatchesCurrentTrigger) return null;

    const products = this.#productsForOffer(widgetPayload);
    if (!products.length) return null;

    return {
      payload: widgetPayload,
      products: this.#withCurrentProduct(products),
      triggerProductIds: widgetTriggerIds,
    };
  }

  #widgetPayload(widget) {
    return widget?.lbPayload?.widget || widget?.lbPayload || null;
  }

  #offerCandidates(payload) {
    const candidates = [];
    const seen = new Set();
    const addCandidate = (candidate) => {
      if (!candidate || typeof candidate !== 'object' || seen.has(candidate)) return;
      seen.add(candidate);
      candidates.push(candidate);
    };

    addCandidate(payload);

    for (const key of ['offer', 'activeOffer', 'selectedOffer', 'bundle', 'bundleOffer', 'widget']) {
      addCandidate(payload?.[key]);
    }

    for (const key of ['offers', 'offerGroups', 'bundles', 'bundleOffers', 'widgets']) {
      const value = payload?.[key];
      if (!Array.isArray(value) || value.length === 0 || value.some((item) => this.#looksLikeProduct(item))) continue;
      value.forEach(addCandidate);
    }

    return candidates;
  }

  #mergeInheritedOfferPayload(widgetPayload, offerPayload) {
    if (widgetPayload === offerPayload) return offerPayload;

    return {
      ...widgetPayload,
      ...offerPayload,
      contents: offerPayload.contents || widgetPayload.contents,
      discounts: offerPayload.discounts || widgetPayload.discounts,
    };
  }

  #productsForOffer(offerPayload) {
    const products = [];
    const seen = new Set();
    const productArrayKeys = [
      'offers',
      'products',
      'offeredProducts',
      'offerProducts',
      'bundleProducts',
      'items',
      'selectedProducts',
    ];

    for (const key of productArrayKeys) {
      const value = offerPayload?.[key];
      if (!Array.isArray(value)) continue;

      for (const item of value) {
        const product = this.#normalizeProductEntry(item);
        const productId = this.#productId(product);
        if (!product || !productId || seen.has(productId)) continue;

        seen.add(productId);
        products.push(product);
      }
    }

    return products;
  }

  #normalizeProductEntry(item) {
    if (!item || typeof item !== 'object') return null;
    if (this.#looksLikeProduct(item)) return item;

    for (const key of ['product', 'productData', 'offerProduct', 'variantProduct']) {
      if (this.#looksLikeProduct(item[key])) return { ...item[key], quantity: item.quantity || item[key].quantity };
    }

    return null;
  }

  #looksLikeProduct(value) {
    if (!value || typeof value !== 'object') return false;
    return Boolean(
      Array.isArray(value.variants) ||
        value.product_id ||
        value.productId ||
        (value.id && (value.title || value.handle || value.featured_image || value.featuredImage || value.image))
    );
  }

  #withCurrentProduct(products) {
    if (!this.#currentProduct) return products;

    const currentProductId = this.dataset.productId?.toString();
    if (!currentProductId || products.some((product) => this.#productId(product) === currentProductId)) return products;

    return [this.#currentProduct, ...products];
  }

  #triggerProductIds(widget, payload) {
    const ids = [
      widget?.getAttribute?.('lb-trigger-product-id'),
      widget?.getAttribute?.('data-trigger-product-id'),
      widget?.getAttribute?.('trigger-product-id'),
      payload?.triggerProductId,
      payload?.trigger_product_id,
      payload?.trigger_product?.id,
      payload?.trigger_product?.product_id,
      payload?.triggerProduct?.id,
      payload?.triggerProduct?.product_id,
      payload?.triggerProduct?.productId,
    ];

    this.#collectTriggerIds(payload, ids);

    return [...new Set(ids.map((id) => this.#normalizeId(id)).filter(Boolean))];
  }

  #collectTriggerIds(value, ids, depth = 0) {
    if (!value || typeof value !== 'object' || depth > 3) return;

    for (const [key, item] of Object.entries(value)) {
      const normalizedKey = key.toLowerCase();
      if (normalizedKey.includes('trigger') && normalizedKey.includes('product')) {
        if (item && typeof item === 'object') {
          if (Array.isArray(item)) {
            item.forEach((entry) => {
              ids.push(this.#productId(entry));
              this.#collectTriggerIds(entry, ids, depth + 1);
            });
          } else {
            ids.push(this.#productId(item));
            this.#collectTriggerIds(item, ids, depth + 1);
          }
        } else {
          ids.push(item);
        }
        continue;
      }

      if (normalizedKey.includes('trigger') && item && typeof item === 'object') {
        ids.push(this.#productId(item));
        this.#collectTriggerIds(item, ids, depth + 1);
        continue;
      }

      if (item && typeof item === 'object' && !this.#looksLikeProduct(item)) {
        this.#collectTriggerIds(item, ids, depth + 1);
      }
    }
  }

  #idsIncludeCurrentProduct(ids) {
    const currentProductId = this.#normalizeId(this.dataset.productId);
    return Boolean(currentProductId && ids.some((id) => this.#normalizeId(id) === currentProductId));
  }

  #normalizeId(value) {
    if (value == null) return '';

    const id = String(value).trim();
    const match = id.match(/(\d+)$/);
    return match ? match[1] : id;
  }

  #offerKey(offerPayload) {
    return (
      offerPayload?.id ||
      offerPayload?.offerId ||
      offerPayload?.offer_id ||
      offerPayload?.bundleId ||
      offerPayload?.bundle_id ||
      offerPayload?.contents?.title ||
      'default'
    ).toString();
  }

  #primeSelectedVariants() {
    for (const product of this.#products) {
      const productId = this.#productId(product);
      if (!productId || this.#selectedVariants.has(productId)) continue;

      const currentVariant = productId === this.dataset.productId?.toString()
        ? this.#variantById(product, this.#currentVariantId)
        : null;
      const variant = currentVariant || this.#availableVariants(product)[0];

      if (variant) this.#selectedVariants.set(productId, variant.id?.toString());
    }
  }

  get #payload() {
    return this.#activeOffer?.payload || this.#widgetPayload(this.#widget);
  }

  get #products() {
    return (this.#activeOffer?.products || []).filter((product) => product && product.available !== false);
  }

  #render() {
    if (!this.#payload || !this.#products.length) {
      this.hidden = true;
      return;
    }

    const selected = this.#selectedProducts;
    const hasMissingSelection = selected.length !== this.#products.length;
    const heading = this.#payload.contents?.title || this.dataset.fallbackHeading || 'Bundle & Save';
    const actionText = this.#payload.contents?.bundleAction?.actionText || 'Add bundle to cart';
    const summaryTitle = this.#payload.contents?.bundleAction?.title || 'Total';
    const pricing = this.#pricing(selected);

    this.hidden = false;
    this.innerHTML = `
      <div class="luminomo-selleasy-bundle__inner">
        <div class="luminomo-selleasy-bundle__header">
          <h2 class="luminomo-selleasy-bundle__heading">${this.#escape(heading)}</h2>
        </div>

        <div class="luminomo-selleasy-bundle__panel">
          <div class="luminomo-selleasy-bundle__items">
            ${this.#products.map((product, index) => this.#itemMarkup(product, index)).join('')}
          </div>

          <div class="luminomo-selleasy-bundle__summary">
            <div class="luminomo-selleasy-bundle__summary-copy">
              <span class="luminomo-selleasy-bundle__summary-label">${this.#escape(summaryTitle)}</span>
              <span class="luminomo-selleasy-bundle__summary-prices">
                ${
                  pricing.originalTotal > pricing.discountedTotal
                    ? `<s class="luminomo-selleasy-bundle__compare">${this.#formatMoney(pricing.originalTotal)}</s>`
                    : ''
                }
                <strong class="luminomo-selleasy-bundle__price">${this.#formatMoney(pricing.discountedTotal || pricing.originalTotal)}</strong>
              </span>
            </div>
            ${
              pricing.savings > 0
                ? `<div class="luminomo-selleasy-bundle__savings">Save ${this.#formatMoney(pricing.savings)}</div>`
                : ''
            }
          </div>

          <button
            class="luminomo-selleasy-bundle__button button"
            type="button"
            data-bundle-add
            ${hasMissingSelection || this.#isAdding ? 'disabled' : ''}
          >
            ${this.#isAdding ? 'Adding...' : this.#escape(actionText)}
          </button>
          <div class="luminomo-selleasy-bundle__status" data-bundle-status aria-live="polite"></div>
        </div>
      </div>
    `;

    this.querySelector('[data-bundle-add]')?.addEventListener('click', () => this.#addBundle());
    this.querySelectorAll('[data-bundle-variant]').forEach((select) => {
      select.addEventListener('change', (event) => {
        const target = event.currentTarget;
        this.#selectedVariants.set(target.dataset.productId, target.value);
        this.#render();
      });
    });
  }

  #itemMarkup(product, index) {
    const productId = this.#productId(product);
    const selectedVariant = this.#selectedVariant(product);
    const variants = this.#availableVariants(product);
    const hasVariantChoices = variants.length > 1 && !variants.every((variant) => this.#isDefaultVariant(variant));
    const image = product.featured_image || product.featuredImage || product.image || product.images?.[0];

    return `
      ${index > 0 ? '<span class="luminomo-selleasy-bundle__plus" aria-hidden="true">+</span>' : ''}
      <article class="luminomo-selleasy-bundle__item">
        <div class="luminomo-selleasy-bundle__media">
          ${
            image
              ? `<img src="${this.#escape(this.#imageUrl(image))}" alt="${this.#escape(product.title || '')}" loading="lazy">`
              : '<span class="luminomo-selleasy-bundle__media-fallback"></span>'
          }
        </div>
        <div class="luminomo-selleasy-bundle__item-copy">
          <h4 class="luminomo-selleasy-bundle__item-title">${this.#escape(product.title || '')}</h4>
          ${
            hasVariantChoices
              ? `<label class="luminomo-selleasy-bundle__variant">
                  <span class="visually-hidden">Choose ${this.#escape(product.title || '')} option</span>
                  <select data-bundle-variant data-product-id="${this.#escape(productId || '')}">
                    ${variants
                      .map(
                        (variant) => `<option value="${variant.id}" ${variant.id?.toString() === selectedVariant?.id?.toString() ? 'selected' : ''}>
                          ${this.#escape(variant.title || variant.public_title || 'Default')}
                        </option>`
                      )
                      .join('')}
                  </select>
                </label>`
              : ''
          }
          <span class="luminomo-selleasy-bundle__item-price">${this.#formatMoney(this.#variantPrice(selectedVariant, product))}</span>
        </div>
      </article>
    `;
  }

  async #addBundle() {
    if (!this.#widget || this.#isAdding) return;

    const selectedProducts = this.#selectedProducts;
    if (selectedProducts.length !== this.#products.length) {
      this.#setStatus('Choose available options before adding the bundle.');
      return;
    }

    const handlers = window.lbHandlers?.widget;
    if (!handlers?.processWidgets) {
      this.#setStatus('Bundle is still loading. Please try again.');
      return;
    }

    this.#isAdding = true;
    this.#render();

    try {
      const widgetProxy = {
        lbPayload: this.#widget.lbPayload,
        getAttribute: (name) => this.#widget.getAttribute(name),
        getSelectedProducts: () => Promise.resolve(selectedProducts),
        updateWidgetState: (...args) => this.#widget.updateWidgetState?.(...args),
      };

      await handlers.processWidgets([{ widgetSrcElement: widgetProxy }]);
      this.#setStatus(this.#payload?.contents?.bundleAction?.afterActionText || 'Bundle added to the cart.');
    } catch (error) {
      console.error('Failed to add Selleasy bundle:', error);
      this.#setStatus('Bundle could not be added. Please try again.');
    } finally {
      this.#isAdding = false;
      this.#render();
    }
  }

  get #selectedProducts() {
    return this.#products
      .map((product) => {
        const variant = this.#selectedVariant(product);
        const productId = this.#productId(product);
        if (!variant || !productId) return null;

        return {
          ...product,
          ...variant,
          id: Number(variant.id),
          productId: Number(productId),
          price: this.#variantPrice(variant, product),
          quantity: product.quantity || 1,
          sellingPlanId: variant.sellingPlanId || product.sellingPlanId,
        };
      })
      .filter(Boolean);
  }

  #selectedVariant(product) {
    const productId = this.#productId(product);
    const selectedId = productId ? this.#selectedVariants.get(productId) : null;
    return this.#variantById(product, selectedId) || this.#availableVariants(product)[0] || null;
  }

  #variantById(product, variantId) {
    if (!variantId) return null;
    return this.#availableVariants(product).find((variant) => variant.id?.toString() === variantId.toString()) || null;
  }

  #availableVariants(product) {
    return (product.variants || []).filter((variant) => variant && variant.available !== false);
  }

  #productId(product) {
    return this.#normalizeId(product?.product_id || product?.productId || product?.id);
  }

  #variantPrice(variant, product) {
    return Number(variant?.price ?? product?.price ?? 0);
  }

  #pricing(selectedProducts) {
    const originalTotal = selectedProducts.reduce((total, product) => total + Number(product.price || 0), 0);
    const discountPercent = this.#discountPercent(selectedProducts.length);
    const discountedTotal = discountPercent > 0 ? Math.round((originalTotal * (100 - discountPercent)) / 100) : originalTotal;

    return {
      originalTotal,
      discountedTotal,
      savings: Math.max(originalTotal - discountedTotal, 0),
      discountPercent,
    };
  }

  #discountPercent(productCount) {
    const tiers = this.#payload?.discounts?.tiers;
    if (!tiers) return 0;

    return Object.entries(tiers).reduce((best, [quantity, discount]) => {
      const tierQuantity = Number(quantity);
      const tierDiscount = Number(discount);
      return productCount >= tierQuantity && tierDiscount > best ? tierDiscount : best;
    }, 0);
  }

  #setStatus(message) {
    const status = this.querySelector('[data-bundle-status]');
    if (status) status.textContent = message;
  }

  #formatMoney(cents) {
    const currency = this.dataset.currencyCode || window.Shopify?.currency?.active || 'USD';

    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency,
      }).format(Number(cents || 0) / 100);
    } catch (error) {
      return `$${(Number(cents || 0) / 100).toFixed(2)}`;
    }
  }

  #imageUrl(image) {
    if (typeof image === 'string') return image;
    return image.src || image.url || '';
  }

  #isDefaultVariant(variant) {
    return !variant?.title || variant.title === 'Default Title';
  }

  #readCurrentProduct() {
    const script = this.querySelector('script[data-current-product]');
    if (!script?.textContent?.trim()) return null;

    try {
      return JSON.parse(script.textContent);
    } catch (error) {
      this.#debug('Could not parse current product JSON.', { error });
      return null;
    }
  }

  #debug(reason, details = {}) {
    let debugEnabled = this.dataset.debug === 'true' || window.LuminomoSelleasyBundleDebug === true;

    try {
      debugEnabled = debugEnabled || window.localStorage?.getItem('luminomoSelleasyDebug') === '1';
    } catch (error) {
      // Ignore storage access errors in restricted browsing contexts.
    }

    if (!debugEnabled) return;

    const widgets = details.widgets || (details.widget ? [details.widget] : []);
    const widget = details.widget || widgets[0] || null;
    const offer = details.offer || this.#activeOffer;
    const products = offer?.products || [];

    console.info('[Luminomo Selleasy Bundle]', {
      reason,
      currentProduct: {
        handle: this.dataset.productHandle || '',
        id: this.dataset.productId || '',
        variantId: this.#currentVariantId || '',
      },
      hiddenSelleasyWidgetExists: widgets.length > 0 || Boolean(widget),
      lbPayloadExists: widgets.some((candidate) => Boolean(candidate?.lbPayload)) || Boolean(widget?.lbPayload),
      detectedOffer: {
        id: this.#offerKey(offer?.payload),
        title: offer?.payload?.contents?.title || offer?.payload?.title || '',
      },
      detectedTriggerProducts: offer?.triggerProductIds || [],
      detectedOfferedProducts: products.map((product) => ({
        id: this.#productId(product),
        title: product.title || '',
        handle: product.handle || '',
      })),
      details,
    });
  }

  #escape(value) {
    return String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }
}

if (!customElements.get('luminomo-selleasy-bundle')) {
  customElements.define('luminomo-selleasy-bundle', LuminomoSelleasyBundle);
}
